import { useState } from 'react';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { criarOperacaoManual } from '@/mocks/backoffice/operacoesHelpers';

export default function BackofficeOperacoesManuaisPage() {
  const { session } = useBackofficeAuth();
  const [tipo, setTipo] = useState<string>('credito_pontos');
  const [usuarioNome, setUsuarioNome] = useState('');
  const [usuarioTipo, setUsuarioTipo] = useState<'empresa' | 'participante'>('participante');
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioNome.trim() || !valor.trim() || !descricao.trim()) return;

    const numValor = parseFloat(valor.replace(',', '.'));
    if (isNaN(numValor) || numValor <= 0) return;

    const isDinheiro = tipo === 'saque_pix' || tipo === 'estorno';
    criarOperacaoManual({
      tipo: tipo as any,
      valor: numValor,
      valor_formatado: isDinheiro
        ? `R$ ${numValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
        : `${numValor.toLocaleString('pt-BR')} pts`,
      usuario_nome: usuarioNome.trim(),
      usuario_tipo: usuarioTipo,
      descricao: descricao.trim(),
    });

    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setUsuarioNome('');
      setValor('');
      setDescricao('');
      setTipo('credito_pontos');
    }, 3000);
  };

  return (
    <BackofficeLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Operações Manuais</h1>
          <p className="text-sm text-gray-500 mt-1">Creditar/debitar pontos ou ajustar saldos manualmente</p>
        </div>

        {submitted && (
          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-emerald-600"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-emerald-800">Operação criada com sucesso!</p>
              <p className="text-xs text-emerald-600">A operação foi enviada para aprovação.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 border border-gray-100 space-y-5">
          {/* Tipo */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-2">Tipo de Operação</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { id: 'credito_pontos', label: 'Crédito de Pontos', icon: 'ri-add-circle-line' },
                { id: 'debito_pontos', label: 'Débito de Pontos', icon: 'ri-indeterminate-circle-line' },
                { id: 'ajuste_saldo', label: 'Ajuste de Saldo', icon: 'ri-equalizer-line' },
                { id: 'saque_pix', label: 'Saque Manual PIX', icon: 'ri-money-cny-circle-line' },
                { id: 'estorno', label: 'Estorno', icon: 'ri-refund-line' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setTipo(opt.id)}
                  className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all cursor-pointer border ${
                    tipo === opt.id
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <i className={opt.icon}></i>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Usuário */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Nome do Usuário</label>
              <input
                type="text"
                value={usuarioNome}
                onChange={(e) => setUsuarioNome(e.target.value)}
                placeholder="Nome completo"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Tipo de Usuário</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setUsuarioTipo('participante')}
                  className={`flex-1 py-2.5 text-xs font-bold transition-all cursor-pointer ${
                    usuarioTipo === 'participante'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Participante
                </button>
                <button
                  type="button"
                  onClick={() => setUsuarioTipo('empresa')}
                  className={`flex-1 py-2.5 text-xs font-bold transition-all cursor-pointer ${
                    usuarioTipo === 'empresa'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Empresa
                </button>
              </div>
            </div>
          </div>

          {/* Valor */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Valor</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="Ex: 1500,00"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                required
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">
                {tipo === 'credito_pontos' || tipo === 'debito_pontos' || tipo === 'ajuste_saldo' ? 'pts' : 'R$'}
              </span>
            </div>
          </div>

          {/* Descrição / Justificativa */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Descrição / Justificativa</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva o motivo desta operação..."
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all resize-none"
              required
            />
            <p className="text-[10px] text-gray-400 mt-1 text-right">{descricao.length}/500</p>
          </div>

          {/* Operador */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Operação será registrada por:</p>
            <p className="text-xs font-bold text-gray-700">{session.nome} ({session.role})</p>
          </div>

          <button
            type="submit"
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            Criar Operação
          </button>
        </form>
      </div>
    </BackofficeLayout>
  );
}